/* @flow */

import type AuthenticationStore, { ResponseOptions } from 'stores/AuthenticationStore';
import { errorHandlerService } from 'services/ServiceRegistry';

/**
 * ApiService is responsible for making HTTP calls through AuthenticationStore helpers.
 * Hardened to avoid overwriting a valid local session (token) on transient/invalid responses.
 */
class ApiService {
  role: ?string = null;
  oAuthStore: AuthenticationStore;

  constructor(oAuthStore: AuthenticationStore) {
    this.oAuthStore = oAuthStore;
  }

  getResource(path: string, responseOptions?: ResponseOptions): * {
    try {
      const res: Promise<*> = this.oAuthStore.getJsonWithToken(path, undefined, responseOptions);
      return res;
    } catch (e) {
      errorHandlerService.handleException(e);
      return null;
    }
  }

  getPagedResource(path: string): * {
    return this.oAuthStore.getJsonWithToken(path);
  }

  getBlobResource(path: string): * {
    const headers: Headers = new Headers();
    headers.set('Accept', 'text/csv');

    const config: Object = { headers };
    return this.oAuthStore.getDataWithToken(path, config);
  }

  getGlobalResource(path: string, type: string, shouldGetBlob: boolean = false): * {
    const headers: Headers = new Headers();
    headers.set('Accept', type);

    const config: Object = { headers };
    return shouldGetBlob
      ? this.oAuthStore.getBlobWithToken(path, config)
      : this.oAuthStore.getDataWithToken(path, config);
  }

  getPdfResource(path: string): * {
    const headers: Headers = new Headers();
    headers.set('Accept', 'application/pdf');
    const config: Object = { headers };
    return this.oAuthStore.getBlobWithToken(path, config);
  }

  getZipResource(path: string): * {
    const headers: Headers = new Headers();
    headers.set('Accept', 'application/octet-stream');
    const config: Object = { headers };
    return this.oAuthStore.getBlobWithToken(path, config);
  }

  putResource(path: string, data: *, responseOptions?: ResponseOptions): Promise<*> {
    return this.oAuthStore.getJsonWithToken(
      path,
      { body: JSON.stringify(data), method: 'PUT' },
      responseOptions
    );
  }

  putFormDataResource(path: string, data: *): Promise<*> {
    return this.oAuthStore.getJsonWithToken(path, { body: data, method: 'PUT' });
  }

  deleteResource(path: string, data: *): Promise<*> {
    return this.oAuthStore.getJsonWithToken(path, { body: JSON.stringify(data), method: 'DELETE' });
  }

  /**
   * Perform API call without credentials (guest).
   * We propagate the backend error payload to the caller.
   */
  async createResourceGuest(path: string, data: *): Promise<*> {
    try {
      const headers: Headers = new Headers();
      headers.set('Content-Type', 'application/json');
      headers.set('Accept', 'application/json');

      const res: Object = await this.oAuthStore.getJsonGuest(path, {
        body: JSON.stringify(data),
        method: 'POST',
        headers,
      });

      return res;
    } catch (e) {
      return Promise.reject(this.oAuthStore.errorResponse || e);
    }
  }

  /**
   * Perform API call without credentials (guest) and custom headers.
   * NOTE (EN): We propagate the backend error payload to the caller.
   */
  async createResourceGuestWithHeaders(path: string, data: *, headersObj: Object): Promise<*> {
    try {
      const headers: Headers = new Headers();
      headers.set('Content-Type', 'application/json');
      headers.set('Accept', 'application/json');

      Object.keys(headersObj || {}).forEach(k => {
        headers.set(k, String(headersObj[k]));
      });

      const res: Object = await this.oAuthStore.getJsonGuest(path, {
        body: JSON.stringify(data),
        method: 'POST',
        headers,
      });

      return res;
    } catch (e) {
      return Promise.reject(this.oAuthStore.errorResponse || e);
    }
  }

  async createResource(path: string, data: *, responseOptions?: ResponseOptions): Promise<*> {
    try {
      const res: Object = await this.oAuthStore.getJsonWithToken(
        path,
        { body: JSON.stringify(data), method: 'POST' },
        responseOptions
      );
      return res;
    } catch (e) {
      errorHandlerService.handleException(e);
      return Promise.reject(this.oAuthStore.errorResponse || e);
    }
  }

  async createFileResource(path: string, data: *, vHeaders: *): Promise<*> {
    try {
      const res: Object = await this.oAuthStore.getJsonWithTokenHeader(
        path,
        { body: data, method: 'POST' },
        vHeaders
      );
      return res;
    } catch (e) {
      errorHandlerService.handleException(e);
      return Promise.reject(this.oAuthStore.errorResponse || e);
    }
  }

  async loginWithMagicLink(data: {
    access_token: string,
    refresh_token: string,
    expires_in: number,
  }) {
    await this.oAuthStore.createTokenFromMagicToken(
      data.access_token,
      data.refresh_token,
      data.expires_in,
      data
    );
    return { success: true };
  }

  /**
   * Bridge-only:
   * Validate B2C one-time token via HttpOnly cookie set by the backend bridge,
   * and create a front-end session token.
   *
   * IMPORTANT:
   * - We do NOT send the token in the request body.
   * - AuthenticationStore MUST send the request with credentials so cookies are included.
   * - We NEVER overwrite localStorage unless we have a valid bearer payload.
   */
  async loginWithJwtToken() {
    this.oAuthStore._authenticationInProgress = true;

    try {
      if (this.oAuthStore.isUserLoggedIn && this.oAuthStore.isUserLoggedIn()) {
        return { success: true };
      }

      const credentials = await this.oAuthStore.getJsonGuest(
        'b2c-token/validate',
        {
          method: 'POST',
          headers: (() => {
            const headers: Headers = new Headers();
            headers.set('Content-Type', 'application/json');
            headers.set('Accept', 'application/json');
            headers.set('X-SSO-Client', 'spa');
            return headers;
          })(),
          body: JSON.stringify({}),
        },
        { fullResponse: true }
      );

      const normalized = this._normalizeB2CValidateResponse(credentials);

      if (!normalized.success || !normalized.access_token) {
        return Promise.reject({
          success: false,
          message:
            normalized.message ||
            'We could not complete your sign-in. Please try again or contact your administrator.',
        });
      }

      const tokenData = {
        access_token: normalized.access_token,
        refresh_token: normalized.refresh_token || '',
        token_type: normalized.token_type || 'Bearer',
        expires_in: normalized.expires_in || 3600,
      };

      await this.oAuthStore.createToken(tokenData);
      this.oAuthStore.saveTokenToLocal(tokenData);

      return {
        success: true,
        data: credentials && credentials.data ? credentials.data : credentials,
      };
    } catch (error) {
      return Promise.reject(
        error || {
          success: false,
          message:
            'We could not complete your sign-in. Please try again or contact your administrator.',
        }
      );
    } finally {
      this.oAuthStore._authenticationInProgress = false;
    }
  }

  isLoggedIn(): boolean {
    return this.oAuthStore.isUserLoggedIn();
  }

  getRole(): ?string {
    return this.role;
  }

  // -----------------------------
  // Private helpers
  // -----------------------------

  _normalizeB2CValidateResponse(
    res: any
  ): {
    success: boolean,
    access_token?: string,
    refresh_token?: string,
    token_type?: string,
    expires_in?: number,
    message?: string,
  } {
    const out = {
      success: false,
      access_token: undefined,
      refresh_token: undefined,
      token_type: undefined,
      expires_in: undefined,
      message: undefined,
    };

    if (!res) return out;

    let success = typeof res.success === 'boolean' ? res.success : true;

    if (res && (res.error || res.errors)) {
      success = false;
    }

    const data = res.data || res;
    const message = (res && res.message) || (data && data.message);

    const accessTokens = data.accessTokens || data.access_tokens || null;
    const tokenDirect = data.token || null;

    if (accessTokens && accessTokens.access_token) {
      return {
        success,
        access_token: accessTokens.access_token,
        refresh_token: accessTokens.refresh_token,
        token_type: accessTokens.token_type || 'Bearer',
        expires_in: accessTokens.expires_in,
        message,
      };
    }

    if (tokenDirect && typeof tokenDirect === 'string') {
      return {
        success,
        access_token: tokenDirect,
        token_type: 'Bearer',
        expires_in: data.expires_in || res.expires_in,
        message,
      };
    }

    if (data.access_token && typeof data.access_token === 'string') {
      return {
        success,
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        token_type: data.token_type || 'Bearer',
        expires_in: data.expires_in,
        message,
      };
    }

    return {
      success: false,
      message: message || 'Unable to parse B2C validate response. Expected a bearer token field.',
    };
  }
}

export default ApiService;
